import { FC } from "react";
import { useTranslation } from "react-i18next";
import TargetIconSVG from "../../../svg/TargetIconSVG";
import ClockIconSVG from "../../../svg/ClockIconSVG";
import CustomCheckbox from "../../../../../custom-components/form-inputs/CustomCheckbox";
import { FormikProps } from "formik";
import CheckMarkIconSVG from "../../../svg/CheckMarkIconSVG";
import WorkCategoriesPopup from "../../popups/WorkCategoriesPopup";
import ClosingCrossLightGrey from "../../../svg/functional/ClosingCrossLightGreySVG";
import { WorksCategoriesForm } from "../../../../../domain/models/dto/WorksCategoriesDTO";
import { WorkSearchSortForm } from "../../../../../domain/models/dto/WorkDTO";
import { WorkSortingFields } from "../../../../../domain/enums/entity-fields/WorkSortingFields";
import WorkSortingTooltip from "./WorkSortingTooltip";
import CurrencyIconSVG from "../../../svg/CurrencyIconSVG";

interface CatalogSortDesktopProps {
    form: FormikProps<WorkSearchSortForm>;
    disableInactiveCheckbox?: boolean;
    showNonActiveCheckboxHandler: () => void;
    workCategoriesHandler: (values: WorksCategoriesForm[]) => void;
    unselectCategoryHandler: (value: WorksCategoriesForm) => void;
    onOpenWorkCategoriesPopup: () => void;
    resetFormCallback: () => Promise<void>;
    remainingCategories: number;
    sortingType: WorkSortingFields;
    setSortingType: (value: WorkSortingFields) => void;
    openingHandler: (value: WorkSortingFields) => void;
}

const CatalogSortDesktop: FC<CatalogSortDesktopProps> = (props) => {
    const { t } = useTranslation();

    return (
        <div className="relative flex flex-col w-full">
            <div className="flex w-full items-center justify-between mb-7 dmax2:mb-5">
                <div className="flex items-center">
                    <span className="H20B dmax2:H16B text-textFaded mr-11 dmax2:mr-5">{t("catalog.sort.label")}</span>
                    <div className="flex gap-9 dmax2:gap-6">
                        <WorkSortingTooltip
                            form={props.form}
                            sortingType={WorkSortingFields.PRICE}
                            currentSortingType={props.sortingType}
                            openingHandler={props.openingHandler}
                            setSortingType={props.setSortingType}
                            buttonElement={
                                <>
                                    <CurrencyIconSVG className="text-textFaded group-hover:text-textWhite mr-3 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-3">
                                        {t(`catalog.sort.${WorkSortingFields.PRICE}`)}
                                    </span>
                                </>
                            }
                        />
                        <WorkSortingTooltip
                            form={props.form}
                            sortingType={WorkSortingFields.DISTANCE}
                            currentSortingType={props.sortingType}
                            openingHandler={props.openingHandler}
                            setSortingType={props.setSortingType}
                            buttonElement={
                                <>
                                    <TargetIconSVG className="text-textFaded group-hover:text-textWhite mr-3 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-3">
                                        {t(`catalog.sort.${WorkSortingFields.DISTANCE}`)}
                                    </span>
                                </>
                            }
                        />
                        <WorkSortingTooltip
                            form={props.form}
                            sortingType={WorkSortingFields.START_FROM}
                            currentSortingType={props.sortingType}
                            openingHandler={props.openingHandler}
                            setSortingType={props.setSortingType}
                            buttonElement={
                                <>
                                    <ClockIconSVG className="text-textFaded group-hover:text-textWhite mr-3 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-3">
                                        {t(`catalog.sort.${WorkSortingFields.START_FROM}`)}
                                    </span>
                                </>
                            }
                        />
                    </div>
                </div>
                {!props.disableInactiveCheckbox && (
                    <div className="flex items-center">
                        <CustomCheckbox
                            labelKey="catalog.sort.showInactive"
                            checked={props.form.values.showInactive}
                            onClick={props.showNonActiveCheckboxHandler}
                            labelClassName="H16 td4:H14B text-textFaded"
                            checkboxClassName="flex justify-center items-center w-6 h-6 border-2 border-textFaded rounded-sm cursor-pointer transition duration-200 ml-4 tmax4:ml-3"
                            checkboxCheckedClassName="!border-0 bg-textWarning"
                            wrapClassName="flex flex-row-reverse items-center"
                            additionalLayoutElement={<CheckMarkIconSVG />}
                        />
                    </div>
                )}
            </div>
            <div className="relative flex items-center di6:items-start justify-between">
                <div className="relative">
                    <div className="relative flex flex-wrap items-center w-full gap-y-3">
                        <WorkCategoriesPopup
                            defaultSelected={props.form.values.categories}
                            confirmButtonTitle={t("workCreation.categoryConfirmation")}
                            confirmButtonClassName="w-[315px] dmax2:w-[225px] h-[50px] dmax2:h-[40px] mt-8 dmax2:mt-6"
                            buttonElement={
                                <div className="button dmin2:H16B dmax2:H14B dmin2:w-[225px] dmax2:w-[175px] dmin2:h-[50px] dmax2:h-[40px] mr-5 transition duration-300">
                                    {t("workCreation.fields.categoryButtonLabel")}
                                </div>
                            }
                            buttonElementClassName="relative"
                            confirmHandler={props.workCategoriesHandler}
                            onopenPopupHandler={props.onOpenWorkCategoriesPopup}
                        />
                        {props.form.values.categories.slice(0, 2).map((item, index) => (
                            <div key={`chosen-category-${index}`} className="mr-3">
                                <div className="relative flex items-center justify-between border border-textFaded rounded-xl pt-2 dmax2:pt-[6px] pb-[10px] dmax2:pb-2 pl-4 dmax2:pl-3 pr-[40px] dmax2:pr-[34px]">
                                    <span className="H16 dmax2:H14 text-textWhite">
                                        {t(`work.categories.${item.category}`)}
                                    </span>
                                    <ClosingCrossLightGrey
                                        onClick={() => props.unselectCategoryHandler(item)}
                                        className="absolute right-4 dmax2:right-3 cursor-pointer"
                                    />
                                </div>
                            </div>
                        ))}
                        {props.form.values.categories.slice(0, 2)?.length < props.form.values.categories?.length && (
                            <span className="H16B text-textFaded mr-3">
                                {t("workCreation.fields.categoriesOverflowLabel", { value: props.remainingCategories })}
                            </span>
                        )}
                    </div>
                </div>
                <div
                    onClick={props.resetFormCallback}
                    className="cursor-pointer H20B dmax2:H14B text-textFaded hover:text-textWhite text-end tmin3:min-w-[130px] di6:mt-3 transition duration-300"
                >
                    {t("catalog.sort.clear")}
                </div>
            </div>
        </div>
    );
};

export default CatalogSortDesktop;
